import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Star, MessageCircle } from "lucide-react";
import { toast } from "sonner";

function StarRow({ value, onPick, size = 4, interactive = false, testIdPrefix }) {
    return (
        <div className="inline-flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((n) => {
                const filled = n <= Math.round(value || 0);
                const Component = interactive ? "button" : "span";
                const extra = interactive ? { type: "button", onClick: () => onPick?.(n), "data-testid": testIdPrefix ? `${testIdPrefix}-${n}` : undefined } : {};
                return (
                    <Component
                        key={n}
                        className={interactive ? "p-0.5 rounded hover:scale-110 transition" : ""}
                        {...extra}
                    >
                        <Star
                            className={`h-${size} w-${size} ${filled ? "fill-yellow-400 stroke-yellow-500" : "stroke-gray-300"}`}
                            strokeWidth={1.5}
                        />
                    </Component>
                );
            })}
        </div>
    );
}

/**
 * ReviewsSection — drops into CourseDetailPage / ProductDetailPage.
 * Renders summary + list + write-form (gated by purchase via /reviews/can-review).
 */
export default function ReviewsSection({ itemId, itemKind, onOpenAuth }) {
    const { user } = useAuth();
    const isAuthed = user && typeof user === "object";

    const [reviews, setReviews] = useState([]);
    const [stats, setStats] = useState({ avg_rating: null, review_count: 0 });
    const [eligibility, setEligibility] = useState(null);
    const [showForm, setShowForm] = useState(false);

    const reloadPublic = () => {
        api.get("/reviews", { params: { item_id: itemId, item_kind: itemKind } }).then(({ data }) => {
            setReviews(data.items || []);
            setStats({ avg_rating: data.avg_rating, review_count: data.review_count });
        });
    };

    const reloadEligibility = () => {
        if (!isAuthed) { setEligibility(null); return; }
        api.get("/reviews/can-review", { params: { item_id: itemId, item_kind: itemKind } })
            .then(({ data }) => setEligibility(data))
            .catch(() => setEligibility(null));
    };

    useEffect(() => { reloadPublic(); }, [itemId, itemKind]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { reloadEligibility(); }, [itemId, itemKind, isAuthed]); // eslint-disable-line react-hooks/exhaustive-deps

    const ctaLabel = useMemo(() => {
        if (!isAuthed) return "Sign in to write a review";
        if (!eligibility) return null;
        if (eligibility.existing_review) {
            return `Your review · ${eligibility.existing_review.status}`;
        }
        if (!eligibility.purchased) return null; // hide CTA entirely for non-purchasers
        return "Write a review";
    }, [isAuthed, eligibility]);

    return (
        <section className="mt-12" data-testid="reviews-section">
            <div className="flex items-end justify-between gap-3 flex-wrap mb-5">
                <div>
                    <h3 className="font-heading text-2xl font-semibold flex items-center gap-2">
                        <MessageCircle className="h-5 w-5 text-brand-600" /> Reviews
                    </h3>
                    {stats.review_count > 0 ? (
                        <div className="mt-1 flex items-center gap-2 text-sm text-ink-700">
                            <StarRow value={stats.avg_rating || 0} />
                            <span className="font-semibold text-ink-900" data-testid="reviews-avg-rating">{stats.avg_rating?.toFixed(1)}</span>
                            <span>· {stats.review_count} review{stats.review_count === 1 ? "" : "s"}</span>
                        </div>
                    ) : (
                        <p className="mt-1 text-sm text-ink-700">No reviews yet — be the first to leave one.</p>
                    )}
                </div>
                {ctaLabel && (
                    <Button
                        onClick={() => {
                            if (!isAuthed) { onOpenAuth?.(); return; }
                            if (eligibility?.existing_review) return;
                            setShowForm((v) => !v);
                        }}
                        className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta"
                        data-testid="reviews-write-btn"
                    >
                        {ctaLabel}
                    </Button>
                )}
            </div>

            {eligibility?.existing_review && (
                <div className={`rounded-2xl border p-4 mb-5 text-sm ${eligibility.existing_review.status === "approved" ? "border-brand-200 bg-brand-50 text-brand-700" : eligibility.existing_review.status === "rejected" ? "border-accent-200 bg-accent-50 text-accent-700" : "border-amber-200 bg-amber-50 text-amber-800"}`} data-testid="reviews-own-status">
                    Your review is <strong className="font-semibold">{eligibility.existing_review.status}</strong>{eligibility.existing_review.status === "pending" && " — it will appear here once an admin approves it."}
                </div>
            )}

            {showForm && isAuthed && eligibility?.can_review && (
                <ReviewForm
                    itemId={itemId}
                    itemKind={itemKind}
                    onCancel={() => setShowForm(false)}
                    onSubmitted={() => { setShowForm(false); reloadEligibility(); reloadPublic(); }}
                />
            )}

            {reviews.length > 0 && (
                <div className="space-y-4">
                    {reviews.map((r) => (
                        <article key={r.id} className="rounded-2xl bg-white border border-gray-100 p-5" data-testid={`review-${r.id}`}>
                            <div className="flex items-center gap-3 mb-2">
                                <div className="h-9 w-9 rounded-full bg-brand-50 text-brand-700 flex items-center justify-center font-semibold text-sm">
                                    {(r.user_name || "U").charAt(0).toUpperCase()}
                                </div>
                                <div>
                                    <div className="font-semibold text-sm text-ink-900">{r.user_name}</div>
                                    <div className="text-xs text-ink-700 flex items-center gap-2">
                                        <StarRow value={r.rating} />
                                        <span>· {String(r.created_at).slice(0, 10)}</span>
                                    </div>
                                </div>
                            </div>
                            {r.title && <div className="font-heading text-lg text-ink-900">{r.title}</div>}
                            {r.body && <p className="mt-1 text-sm text-ink-700 leading-relaxed whitespace-pre-wrap">{r.body}</p>}
                        </article>
                    ))}
                </div>
            )}
        </section>
    );
}

function ReviewForm({ itemId, itemKind, onCancel, onSubmitted }) {
    const [rating, setRating] = useState(5);
    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!body.trim()) {
            toast.error("Please share a few words about your experience.");
            return;
        }
        setSaving(true);
        try {
            await api.post("/reviews", { item_id: itemId, item_kind: itemKind, rating, title: title.trim(), body: body.trim() });
            toast.success("Review submitted — pending admin approval.");
            onSubmitted?.();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={submit} className="rounded-2xl bg-paper-alt border border-gray-100 p-5 mb-6 space-y-3" data-testid="review-form">
            <div>
                <div className="text-xs uppercase tracking-wider text-ink-400 mb-1.5">Your rating</div>
                <StarRow value={rating} interactive onPick={setRating} size={6} testIdPrefix="review-star" />
            </div>
            <div>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (optional)" className="rounded-xl" maxLength={120} data-testid="review-title-input" />
            </div>
            <div>
                <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="What stood out for you?" rows={4} className="rounded-xl" maxLength={2000} required data-testid="review-body-input" />
            </div>
            <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={onCancel} className="rounded-full">Cancel</Button>
                <Button type="submit" disabled={saving} className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta" data-testid="review-submit-btn">
                    {saving ? "Submitting…" : "Submit review"}
                </Button>
            </div>
        </form>
    );
}
